import type { Ref } from 'react';
import React, {
    useCallback,
    useEffect,
    useImperativeHandle,
    useMemo,
    useRef,
    useState,
} from 'react';
import { Box } from '@mui/material';
import { useHistory, useLocation } from 'react-router-dom';
import { insert, isNil, pick, reject, uniq } from 'ramda';
import type { SearchPayload } from 'c-data-layer';
import { isApiErrorMessage, NetworkRequestState, paginatedDataToSearchPayload } from 'c-data-layer';
import type {
    FilterableEntityTableProps,
    FilterableEntityTableRefAPI,
    FilterableEntityTableRendererProps,
    FilterConfig,
} from 'c-pagination/types';
import { CustomFilterConfigType, FilterableEntityTableRendererType } from 'c-pagination/types';
import { usePaginatedEntityData } from 'c-data';
import { generateRows, generateSearchColumnsData } from 'c-pagination/Lib';
import type { TableUrlState } from 'c-pagination/Lib/tableUrlState';
import {
    fromTableUrlState,
    readTableUrlState,
    searchTermChanged,
    toTableUrlState,
    writeTableUrlState,
} from 'c-pagination/Lib/tableUrlState';
import merge from 'deepmerge';
import { useUserPermissions } from 'c-auth-module/Hooks';
import { usePrevious } from 'react-hanger';
import { useIsMobileView } from 'c-hooks';
import type { ListSearchOptions } from 'c-sdk';
import { PermissionName } from 'c-sdk';
import equal from 'fast-deep-equal/es6';
import {
    useCommonTranslation,
    useCommonTranslationInstance,
    useModelFieldTranslation,
} from 'c-translation';
import type {
    PaginatedEntityTableContextValues,
    SearchTermSetter,
} from 'c-pagination/EntityPaginationContext';
import { PaginatedEntityTableContext } from 'c-pagination/EntityPaginationContext';
import TablePagination from '../TablePagination/TablePagination';
import PaginatedEntityList from '../PaginatedEntityList/PaginatedEntityList';
import EntityFilters from '../EntityFilters/EntityFilters';
import ContainedRenderer from './ContainedRenderer';
import DefaultRenderer from './DefaultRenderer';

type Props = FilterableEntityTableProps;

const searchTermKey = 'search';
const pickFields = [
    'orderBy',
    'direction',
    'filters',
    'perPage',
    'page',
    'includes',
    'searchables',
];
const searchPayloadForComparison = payload => reject(isNil, pick(pickFields, payload));
const defaultRenderIds = ids => ids;
// A shared default so the memos keyed on textSearchColumns don't see a new [] every render.
const noTextSearchColumns: string[] = [];
const dedupedFilters = (options: ListSearchOptions): ListSearchOptions => ({
    ...options,
    filters: Object.entries(options.filters ?? {}).reduce((acc, [filterKey, values]) => {
        if (Array.isArray(values) && values.length > 0)
            return { ...acc, [filterKey]: uniq((values ?? []) as any[]) };

        return acc;
    }, {}),
});

const defaultUpdateSearchPayload: Props['updateSearchPayload'] = payload => payload;

function FilterableEntityTable(
    {
        orderBy = 'id',
        direction = 'desc',
        perPage = 25,
        page = 1,
        showFilters,
        includes,
        filters,
        baseEntityName,
        tag,
        routeTemplate,
        columns,
        resetOnUnmount = false,
        refreshOnMount = true,
        textSearchColumns = noTextSearchColumns,
        revertToIdSearchOnNumberOnlyInput = true,
        // filterFilters = true,
        filterFilters,
        smartFilters = false,
        renderIds = defaultRenderIds,
        onRowClick,
        afterFilters,
        onlyIncludeFilterKeys,
        excludeNullFilterOptions = false,
        standaloneResetFilters = false,
        updateSearchPayload = defaultUpdateSearchPayload,
        customListSearch,
        tableContainerProps,
        tableProps,
        customFilterConfig,
        generateRowSx,
        disabledRowDividers,
        dense,
        CustomRenderer,
        rendererType = FilterableEntityTableRendererType.Default,
        alwaysOpenFilters = false,
        filtersInlineSearch = false,
        displayDownloadButton = false,
        reImportButton = false,
        customFilters = [],
        customFilterResets = [],
        filterApplyButton = false,
        loadBar = true,
        disableCache = false,
        syncToUrl = true,
        emptyMessage,
    }: Props,
    ref: Ref<FilterableEntityTableRefAPI>,
) {
    const { hasAll, isUserOneOfTypes, isAdmin } = useUserPermissions();
    const { paginatedData, getPaginationData, reset } = usePaginatedEntityData(
        tag,
        baseEntityName,
        customListSearch,
    );
    const searchKey = useMemo(() => {
        // just want to pre populate the search input with whatever may have previously been searched for
        const possibleSearchColumns = ['id', ...textSearchColumns];
        const searchedColumns = Object.keys(paginatedData?.searchables ?? {});

        return searchedColumns.find(col =>
            possibleSearchColumns.find(searchCol => col.endsWith(searchCol)),
        );
    }, [paginatedData?.searchables, textSearchColumns]);

    const history = useHistory();
    const { pathname, search: locationSearch } = useLocation();
    const urlEnabled = syncToUrl && !disableCache;
    const urlState = useMemo(
        () => (urlEnabled ? readTableUrlState(locationSearch, tag) : undefined),
        [urlEnabled, locationSearch, tag],
    );

    const [defaultSearchTerm, setDefaultSearchTerm] = useState(() => {
        if (disableCache) return '';
        if (urlState != null) return urlState.q ?? '';
        return paginatedData?.searchables?.[searchKey] ?? '';
    });

    const prevSearchOptions = useRef<SearchPayload<any>>({});
    /**
     * Whether a search went out after the render this commit's callbacks were made in. Reset first
     * thing in every commit's effects. Restoring from the URL on mount searches in the same commit
     * as a page's own mount effects, which then call `search()` with a `paginatedData` from before
     * the restore: `refresh` reads `prevSearchOptions` instead, or it would search the URL away.
     */
    const searchedSinceRender = useRef(false);
    useEffect(() => {
        searchedSinceRender.current = false;
    });
    const tableRef = useRef<HTMLDivElement>();

    const prevLoading = usePrevious(paginatedData?.loadingState?.state);
    const prevPage = usePrevious(paginatedData?.meta?.pagination?.current_page);

    useEffect(() => {
        if (
            paginatedData?.loadingState?.state === NetworkRequestState.Idle &&
            prevLoading === NetworkRequestState.InProgress &&
            paginatedData?.meta?.pagination?.current_page !== prevPage &&
            prevPage != null
        ) {
            tableRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [paginatedData?.loadingState?.state]);

    const defaultSearchPayload = useMemo<SearchPayload<any>>(
        () =>
            reject(isNil, {
                orderBy,
                direction,
                perPage,
                page,
                showFilters,
                filters,
                includes,
            }),
        [orderBy, direction, perPage, page, showFilters, includes, filters],
    );

    const rows = useMemo(
        () =>
            generateRows({
                ids: renderIds(paginatedData.data),
                baseEntityName,
                routeTemplate,
                onClick: onRowClick,
            }),
        [paginatedData.data, baseEntityName, routeTemplate, renderIds, onRowClick],
    );

    /**
     * The table's state as the URL holds it, as far as this table knows: what it last wrote or
     * restored. A location whose state differs from this was changed by someone else (Back,
     * Forward, a link), and the table follows it.
     */
    const urlWritten = useRef<TableUrlState | undefined>(undefined);
    /**
     * The next write rewrites the current history entry rather than adding one: the first write
     * after mounting or restoring only brings the URL in line with what is already on screen, and
     * a push there would leave an entry that Back lands on to no effect.
     */
    const replaceNextUrlWrite = useRef(true);

    const writeUrl = useCallback(
        (criteria: SearchPayload<any>) => {
            if (!urlEnabled) return;

            const next = toTableUrlState(criteria, defaultSearchPayload);
            const replace = replaceNextUrlWrite.current;
            replaceNextUrlWrite.current = false;
            if (equal(next, urlWritten.current)) return;

            // A new search term rewrites the entry: one per keystroke would bury the filter sets
            // Back is for. Filters, paging and ordering are deliberate, so they add one.
            const mode =
                replace || searchTermChanged(urlWritten.current, next) ? 'replace' : 'push';
            urlWritten.current = next;
            history[mode]({
                pathname: history.location.pathname,
                search: writeTableUrlState(history.location.search, tag, next),
            });
        },
        [urlEnabled, defaultSearchPayload, history, tag],
    );

    const onSearch = useCallback(
        (page: number, settings: SearchPayload<any>, showFilters?: true, force = false) => {
            const newSearchCriteria = dedupedFilters(
                updateSearchPayload({
                    ...settings,
                    page,
                    showFilters: filterFilters === true ? true : showFilters,
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    filterFilters,
                    smartFilters,
                }),
            );
            if (
                force ||
                !equal(
                    searchPayloadForComparison(newSearchCriteria),
                    searchPayloadForComparison(prevSearchOptions.current),
                )
            ) {
                prevSearchOptions.current = newSearchCriteria;
                searchedSinceRender.current = true;
                getPaginationData({ ...newSearchCriteria }, true);
                writeUrl(newSearchCriteria);
            }
        },
        [getPaginationData, updateSearchPayload, filterFilters, smartFilters, writeUrl],
    );

    const refresh = useCallback(
        (page?: number) => {
            if (searchedSinceRender.current) {
                const latest = prevSearchOptions.current;
                onSearch(page ?? latest.page ?? 1, latest, true, true);
                return;
            }
            onSearch(
                page ?? paginatedData?.meta?.pagination?.current_page,
                merge<SearchPayload<any>>(defaultSearchPayload, {
                    filters: paginatedData?.filters ?? {},
                    searchables: paginatedData?.searchables ?? {},
                    direction: paginatedData?.direction ?? undefined,
                    orderBy: paginatedData?.orderBy ?? undefined,
                }),
                true,
                true,
            );
        },
        [
            onSearch,
            paginatedData?.meta?.pagination?.current_page,
            paginatedData?.filters,
            paginatedData?.searchables,
            paginatedData?.direction,
            paginatedData?.orderBy,
            defaultSearchPayload,
        ],
    );

    // Filled in by EntityFilters once it mounts; empty when this table renders no search box.
    const searchTermSetterRef = useRef<SearchTermSetter | undefined>();
    const registerSearchTermSetter = useCallback((setter?: SearchTermSetter) => {
        searchTermSetterRef.current = setter;
    }, []);

    const contextValues = useMemo<PaginatedEntityTableContextValues>(
        () => ({
            search: page => refresh(page),
            setSearchTerm: term => searchTermSetterRef.current?.(term),
        }),
        [refresh],
    );

    /**
     * provide a custom ref (useRef) API
     */
    useImperativeHandle(ref, () => contextValues, [contextValues]);

    const onResetFilters = useCallback(() => {
        setDefaultSearchTerm('');

        /**
         * Set default values to new instance of an object.
         * This will auto reset the form.
         */
        // `searchKey` is undefined until something has been searched; `{ [undefined]: '' }` would
        // send a literal `undefined=` param on every fresh table's first request.
        const searchables = searchKey ? { [searchKey]: '' } : {};
        onSearch(1, { ...defaultSearchPayload, searchables }, showFilters);
    }, [onSearch, defaultSearchPayload, showFilters, searchKey]);
    useEffect(() => {
        if (disableCache) {
            setDefaultSearchTerm('');
            onResetFilters();
        }
    }, [disableCache, onResetFilters, setDefaultSearchTerm]);

    /** Search for what a URL state stands for, and show its term in the search box. */
    const restoreFromUrl = useCallback(
        (state: TableUrlState | undefined) => {
            urlWritten.current = state;
            replaceNextUrlWrite.current = true;
            const payload = fromTableUrlState(state, defaultSearchPayload, term =>
                generateSearchColumnsData(
                    textSearchColumns,
                    term,
                    revertToIdSearchOnNumberOnlyInput === true,
                ),
            );
            setDefaultSearchTerm(state?.q ?? '');
            searchTermSetterRef.current?.(state?.q ?? '', { silent: true });
            onSearch(payload.page ?? 1, payload, showFilters);
            // Spent by now if the search went out; if it was deduped, the next write is the user's.
            replaceNextUrlWrite.current = false;
        },
        [
            defaultSearchPayload,
            textSearchColumns,
            revertToIdSearchOnNumberOnlyInput,
            onSearch,
            showFilters,
        ],
    );

    useEffect(() => {
        if (urlState != null) {
            // A bookmarked, shared or refreshed URL wins over whatever the store remembers.
            restoreFromUrl(urlState);
        } else if (paginatedData?.meta?.pagination?.current_page == null) {
            onResetFilters();
        } else if (refreshOnMount) {
            // Back on the list by a link: the store's filters stay, and are written to the URL.
            refresh();
        }
        replaceNextUrlWrite.current = false;
        return () => {
            if (resetOnUnmount) {
                reset();
            }
        };

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const lastPathname = useRef(pathname);
    useEffect(() => {
        if (!urlEnabled) return;

        if (pathname !== lastPathname.current) {
            lastPathname.current = pathname;
            // The table stayed mounted across a route change (a tab that is its own path). The
            // new path's URL has no state for it unless it was written there: carry the current
            // search over, as on mount, rather than resetting the list.
            if (urlState == null) {
                urlWritten.current = undefined;
                replaceNextUrlWrite.current = true;
                writeUrl(prevSearchOptions.current);
                return;
            }
        }
        if (!equal(urlState, urlWritten.current)) {
            restoreFromUrl(urlState);
        }
        // Only a location change is a reason to look again; the callbacks change with every search.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [urlState, pathname]);

    const i18n = useCommonTranslationInstance();
    const mt = useModelFieldTranslation();
    const t = useCommonTranslation();
    const visibleCustomFilters = useMemo<FilterConfig[]>(
        () =>
            (customFilterConfig ?? []).filter(f => {
                if (isAdmin) return true;

                const hasPermissions = hasAll(f.permissions ?? []);
                const hasUserType = isUserOneOfTypes(f.userTypes ?? []);

                return hasPermissions && hasUserType;
            }),
        [customFilterConfig, hasAll, isAdmin, isUserOneOfTypes],
    );

    /**
     * Held by value, not by reference. A self-refreshing table gets a fresh copy of this metadata
     * from every response, and rebuilding the array hands the filter dropdowns brand-new option
     * objects. MUI matches a selected value to its option by identity, so the user's ticked
     * options silently untick on each refresh — and any open dropdown is rebuilt underneath them.
     */
    const lastFilters = useRef<any[]>([]);
    const actualFilters = useMemo(() => {
        const next = !Array.isArray(paginatedData?.meta?.filters)
            ? []
            : paginatedData.meta.filters.filter(
                  f =>
                      f.expose === true &&
                      (onlyIncludeFilterKeys
                          ? onlyIncludeFilterKeys?.indexOf(f.key) !== -1
                          : true) &&
                      (hasAll(f.permissions ?? []) ||
                          hasAll([PermissionName.AdminFilteringBypass_filter_permissions])),
              );

        if (equal(next, lastFilters.current)) {
            return lastFilters.current;
        }
        lastFilters.current = next;

        return next;
    }, [hasAll, paginatedData?.meta?.filters, onlyIncludeFilterKeys]);

    const parsedFilters = useMemo<FilterConfig[]>(() => {
        let allParsedFilters: FilterConfig[] = actualFilters.map(f => {
            const labelKey = `Pagination.filterLabels.${baseEntityName}.${f.key}`;
            return {
                label: i18n.exists(labelKey) ? t(labelKey) : f.label,
                type: CustomFilterConfigType.Select,
                key: f.key,
                permissions: f.permissions,
                options: f.options
                    .filter(opt => !excludeNullFilterOptions || opt.value != null)
                    .map(opt => ({
                        label: mt(baseEntityName, f.key.split('.').pop(), opt.name),
                        value: opt.value,
                        disabled: opt.disabled,
                    })),
            };
        });

        visibleCustomFilters.forEach(f => {
            if (f.insertAtIndex != null)
                allParsedFilters = insert(f.insertAtIndex, f, allParsedFilters);
            else allParsedFilters.push(f);
        });

        return allParsedFilters;
    }, [
        actualFilters,
        visibleCustomFilters,
        t,
        i18n,
        mt,
        baseEntityName,
        excludeNullFilterOptions,
    ]);

    const showFilterSectionColumn = useMemo(() => {
        if (!showFilters) {
            return false;
        }
        if (
            paginatedData?.loadingState?.state === NetworkRequestState.InProgress &&
            parsedFilters?.length === 0
        ) {
            // in the middle of loading the filters
            return false;
        }

        if (parsedFilters.length === 0 && textSearchColumns?.length === 0) {
            return false;
        }

        return true;
    }, [
        paginatedData?.loadingState?.state,
        parsedFilters.length,
        showFilters,
        textSearchColumns?.length,
    ]);

    const onSubmit = useCallback(
        (theSearchTerm: string, force = false) => {
            onSearch(
                1,
                {
                    ...paginatedData,
                    searchables: generateSearchColumnsData(
                        textSearchColumns,
                        theSearchTerm,
                        revertToIdSearchOnNumberOnlyInput === true,
                    ),
                },
                undefined,
                force,
            );
        },
        [onSearch, paginatedData, textSearchColumns, revertToIdSearchOnNumberOnlyInput],
    );

    const actualTableProps = useMemo<typeof tableProps>(() => {
        if (rendererType === FilterableEntityTableRendererType.Contained)
            return { stickyHeader: true, ...tableProps };
        return tableProps;
    }, [tableProps, rendererType]);

    const filterForm = useMemo(
        // sticky headers turn the header row white (so you can't see behind it,
        // but that means there is no visible gap between the filters and the table
        () => (
            <Box mb={actualTableProps?.stickyHeader ? 2 : 0}>
                <EntityFilters
                    entityName={baseEntityName}
                    tag={tag}
                    allFilters={parsedFilters}
                    textSearchColumn={textSearchColumns.length > 0 ? searchTermKey : null}
                    textSearchCols={textSearchColumns ?? []}
                    reset={onResetFilters}
                    onSubmit={onSubmit}
                    defaultSearchVal={String(defaultSearchTerm)}
                    registerSearchTermSetter={registerSearchTermSetter}
                    afterFilters={afterFilters}
                    alwaysOpenFilters={alwaysOpenFilters}
                    standaloneResetFilters={standaloneResetFilters}
                    filtersInlineSearch={filtersInlineSearch}
                    displayDownloadButton={displayDownloadButton}
                    reImportButton={reImportButton}
                    customFilters={customFilters}
                    customFilterResets={customFilterResets}
                    filterApplyButton={filterApplyButton}
                />
            </Box>
        ),
        [
            actualTableProps?.stickyHeader,
            baseEntityName,
            registerSearchTermSetter,
            tag,
            parsedFilters,
            textSearchColumns,
            onResetFilters,
            onSubmit,
            defaultSearchTerm,
            afterFilters,
            alwaysOpenFilters,
            standaloneResetFilters,
            filtersInlineSearch,
            displayDownloadButton,
            reImportButton,
            customFilters,
            customFilterResets,
            filterApplyButton,
        ],
    );

    const filterFormComponent = useMemo(() => {
        if (
            (showFilterSectionColumn && paginatedData?.meta?.filters != null) ||
            textSearchColumns.length > 0
        ) {
            return filterForm;
        }

        if (
            (!showFilters || Object.keys(paginatedData?.meta?.filters ?? {}).length === 0) &&
            textSearchColumns.length > 0
        ) {
            return filterForm;
        }
        return null;
    }, [
        filterForm,
        paginatedData?.meta?.filters,
        showFilterSectionColumn,
        showFilters,
        textSearchColumns.length,
    ]);

    const onPageChange = useCallback(
        (e, pageNumber: number, perPage?: number) => {
            onSearch(
                pageNumber,
                paginatedDataToSearchPayload(paginatedData, {
                    perPage: perPage ?? paginatedData?.meta?.pagination?.per_page,
                }),
            );
        },
        [onSearch, paginatedData],
    );

    const pagination = useMemo(
        () => (
            <TablePagination
                showFirstButton
                showLastButton
                count={paginatedData?.meta?.pagination?.total ?? 0}
                totalPages={paginatedData?.meta?.pagination?.total_pages ?? 0}
                onPageChange={onPageChange}
                page={paginatedData?.meta?.pagination?.current_page ?? 1}
                rowsPerPage={paginatedData?.meta?.pagination?.per_page ?? 0}
                loadingPageNumber={
                    loadBar
                        ? paginatedData?.loadingState?.state === NetworkRequestState.InProgress
                            ? paginatedData?.page
                            : undefined
                        : undefined
                }
            />
        ),
        [
            paginatedData?.meta?.pagination?.total,
            paginatedData?.meta?.pagination?.total_pages,
            paginatedData?.meta?.pagination?.current_page,
            paginatedData?.meta?.pagination?.per_page,
            paginatedData?.loadingState?.state,
            paginatedData?.page,
            onPageChange,
            loadBar,
        ],
    );

    const loading = useMemo(
        () => paginatedData?.loadingState?.state === NetworkRequestState.InProgress,
        [paginatedData],
    );
    const error = useMemo(
        () => paginatedData?.loadingState?.state === NetworkRequestState.Error,
        [paginatedData],
    );
    const genericErrorMessage = useCommonTranslation('Pagination.genericError');
    const errorMessage = useMemo(() => {
        const apiError = paginatedData?.loadingState?.error;
        return isApiErrorMessage(apiError) ? apiError : genericErrorMessage;
    }, [genericErrorMessage, paginatedData]);

    const paginatedEntityTable = useMemo(
        () => (
            <PaginatedEntityList
                onSearch={onSearch}
                tag={tag}
                paginationData={paginatedData}
                columns={columns}
                rows={rows}
                tableContainerProps={tableContainerProps}
                tableProps={actualTableProps}
                baseEntityName={baseEntityName}
                generateRowSx={generateRowSx}
                disabledRowDividers={disabledRowDividers}
                dense={dense}
                emptyMessage={emptyMessage}
            />
        ),
        [
            baseEntityName,
            columns,
            dense,
            emptyMessage,
            disabledRowDividers,
            generateRowSx,
            onSearch,
            paginatedData,
            rows,
            tableContainerProps,
            actualTableProps,
            tag,
        ],
    );
    const rendererProps = useMemo<FilterableEntityTableRendererProps>(
        () => ({
            table: paginatedEntityTable,
            loading: loadBar ? loading : false,
            error: error ? errorMessage : null,
            filterForm: filterFormComponent,
            wrapperComponentRef: tableRef,
            pagination,
        }),
        [
            error,
            errorMessage,
            filterFormComponent,
            loading,
            paginatedEntityTable,
            pagination,
            loadBar,
        ],
    );

    const isMobile = useIsMobileView();
    const Renderer = useMemo(() => {
        if (CustomRenderer != null) return CustomRenderer;

        if (!isMobile && rendererType === FilterableEntityTableRendererType.Contained)
            return ContainedRenderer;

        return DefaultRenderer;
    }, [CustomRenderer, rendererType, isMobile]);

    return (
        <PaginatedEntityTableContext.Provider value={contextValues}>
            <Renderer {...rendererProps} />
        </PaginatedEntityTableContext.Provider>
    );
}

export default React.forwardRef(FilterableEntityTable);
